/**
 * System configuration — tunable business rules and feature toggles.
 * Single source of truth so limits are never hard-coded in screens.
 */

export const SYSTEM_CONFIG_DEFS = [
  {
    key: 'vendorSelfRegistrationEnabled',
    label: 'Self register as vendor',
    description: 'When off, the public vendor registration page is closed.',
    group: 'Vendors',
    type: 'boolean',
    defaultValue: true,
  },
  {
    key: 'vendorSelfRegistrationDisabledReason',
    label: 'Reason (shown when registration is off)',
    description: 'Displayed on the public registration page when self registration is disabled.',
    group: 'Vendors',
    type: 'text',
    defaultValue: '',
    showWhen: { key: 'vendorSelfRegistrationEnabled', equals: false },
    requiredWhen: { key: 'vendorSelfRegistrationEnabled', equals: false },
    minLength: 5,
    maxLength: 500,
  },
  {
    key: 'maxRecommendationsPerRequest',
    label: 'Max recommendations per request',
    description:
      'Maximum number of equipment a staff member can recommend against one indent.',
    group: 'Indents',
    type: 'number',
    min: 1,
    max: 20,
    defaultValue: 3,
  },
  {
    key: 'indenterFormEnabled',
    label: 'Indenter Form / Request',
    description:
      'When off, vendors cannot submit new requests from their portal link and staff cannot raise requests from admin.',
    group: 'Indents',
    type: 'boolean',
    defaultValue: true,
  },
  {
    key: 'defaultPageSize',
    label: 'Default rows per page',
    description: 'Initial page size for list tables.',
    group: 'General',
    type: 'number',
    min: 5,
    max: 200,
    defaultValue: 10,
  },
];

const DEFAULTS = SYSTEM_CONFIG_DEFS.reduce((acc, def) => {
  acc[def.key] = def.defaultValue;
  return acc;
}, {});

let config = { ...DEFAULTS };

function matchesCondition(draft, condition) {
  if (!condition) return true;
  return draft[condition.key] === condition.equals;
}

export function getSystemConfig() {
  return config;
}

export function getConfigValue(key) {
  return config[key] ?? DEFAULTS[key];
}

export function findConfigDef(key) {
  return SYSTEM_CONFIG_DEFS.find((d) => d.key === key) || null;
}

export function isConfigFieldVisible(def, draft) {
  return matchesCondition(draft, def.showWhen);
}

/** Validates against the definition; returns { ok, value, error }. */
export function validateConfigValue(key, raw, draft = {}) {
  const def = findConfigDef(key);
  if (!def) return { ok: false, error: 'Unknown setting' };

  if (!isConfigFieldVisible(def, draft)) {
    return { ok: true, value: def.defaultValue };
  }

  if (def.type === 'boolean') return { ok: true, value: Boolean(raw) };

  if (def.type === 'text') {
    const text = String(raw ?? '').trim();
    const required = matchesCondition(draft, def.requiredWhen);
    if (required && text.length < (def.minLength || 1)) {
      return { ok: false, error: `Enter a reason (min ${def.minLength || 1} characters)` };
    }
    if (def.maxLength && text.length > def.maxLength) {
      return { ok: false, error: `Max ${def.maxLength} characters` };
    }
    return { ok: true, value: text };
  }

  const num = Number(raw);
  if (raw === '' || Number.isNaN(num)) return { ok: false, error: 'Enter a number' };
  if (!Number.isInteger(num)) return { ok: false, error: 'Whole numbers only' };
  if (def.min != null && num < def.min) return { ok: false, error: `Minimum ${def.min}` };
  if (def.max != null && num > def.max) return { ok: false, error: `Maximum ${def.max}` };
  return { ok: true, value: num };
}

export function updateSystemConfig(patch) {
  config = { ...config, ...patch };
  return config;
}

export function resetSystemConfig() {
  config = { ...DEFAULTS };
  return config;
}
